import * as React from 'react';
import { Component } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { AppUser } from 'Src/views/App/App';
import { History, Location } from 'history';
import propz from 'propz';
import { Basket as BasketModel, BasketItem } from 'Src/models/basket';
import { getBasket, deleteOrderFromBasket, createBasketPayment } from 'Src/helpers/service/parent/basket';
import { BasketOrder } from './BasketOrder/BasketOrder';
import { Loader } from 'Src/components/Loader/Loader';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ParentPageHeader } from '../components/ParentPageHeader';
import '../components/ParentLists.scss';
import './Basket.scss';

const MINUTE = 1000 * 5;

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  setNeedUpdate: () => void;
}

interface State {
  isLoading: boolean;
  basket: BasketModel;
  basketOrderWithNearestDeadline: BasketItem;
  orderId: string;
  basketRequestIntervalId: number;
  isConfirmationRemoveFromBasketModalOpen: boolean;
  isDeadlineTimeModalOpen: boolean;
}

export class Basket extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      basket: undefined,
      basketOrderWithNearestDeadline: undefined,
      orderId: undefined,
      basketRequestIntervalId: undefined,
      isConfirmationRemoveFromBasketModalOpen: false,
      isDeadlineTimeModalOpen: false
    };
  }

  componentDidMount() {
    this.setBasket().then(() => this.setBasketRequestInterval());
  }

  componentWillUnmount() {
    const { basketRequestIntervalId } = this.state;

    clearInterval(basketRequestIntervalId);
  }

  onCheckoutClick = () => {
    this.setState({
      isLoading: true
    });

    const { user } = this.props;
    const { basket } = this.state;
    const messageIds = basket.items.map(item => item.messageId);

    const cancelUrl = document.location.href;
    const successUrl = `${document.location.origin}/paymentSuccess`;

    const data = { successUrl, cancelUrl, messageIds };

    let sessionId;
    let publicKey;

    createBasketPayment(user, basket.id, data)
      .then(session => {
        sessionId = session.id;
        publicKey = session.publicKey;
      })
      .then(result => {
        return loadStripe(publicKey);
      })
      .then(stripe => {
        return stripe.redirectToCheckout({
          sessionId: sessionId
        });
      })
      .then(result => {
        this.setState({
          isLoading: false
        });

        if (result.error) {
          alert(result.error.message);
        }
      })
      .catch(err => {
        this.setState({
          isLoading: false
        });

        if (typeof err !== 'undefined') {
          //  TODO: for prod replace on  "contact support"
          const displayMessageText = propz.get(err, ['response', 'data', 'details', 'text']);
          console.log(err);
          alert(`Error: ${displayMessageText}`);
        }
      });
  };

  setBasket = () => {
    const { user, setNeedUpdate } = this.props;

    this.setState({
      isLoading: true
    });

    return getBasket(user).then(basket => {
      this.setState({
        basket,
        isLoading: false
      });

      setNeedUpdate();
    });
  };

  setBasketRequestInterval = () => {
    const { basket } = this.state;

    const isBasketExist = typeof basket !== 'undefined';
    const isBasketOrdersExist = isBasketExist && basket.items.length > 0;

    if (isBasketOrdersExist) {
      const { items } = basket;

      const basketOrderWithNearestDeadline = items.reduce((initial, item) => {
        const itemDeadline = new Date(item.deadline);
        const initialDeadline = new Date(initial.deadline);

        return itemDeadline > initialDeadline ? initial : item;
      });

      const { deadline: nearestDeadline } = basketOrderWithNearestDeadline;

      const nearestDeadlineDate = new Date(nearestDeadline);

      const basketRequestIntervalId = setInterval(() => {
        const now = new Date();

        if (now >= nearestDeadlineDate) {
          this.openDeadlineTimeModal();

          clearInterval(basketRequestIntervalId);
        }
      }, MINUTE);

      this.setState({
        basketRequestIntervalId,
        basketOrderWithNearestDeadline
      });
    }
  };

  onDeleteOrderFromBasketClick = orderId => {
    const { user, setNeedUpdate } = this.props;

    this.setState({
      isLoading: true
    });

    deleteOrderFromBasket(user, orderId).then(() => {
      setNeedUpdate();

      this.closeConfirmationRemoveFromBasketModal();

      this.setBasket();
    });
  };

  renderDeadlineTimeModal = () => {
    const { isDeadlineTimeModalOpen, basketOrderWithNearestDeadline } = this.state;

    const clubName = propz.get(basketOrderWithNearestDeadline, ['clubName'], '');

    return (
      <SimpleModal
        isOpen={isDeadlineTimeModalOpen}
        title="Confirm the action"
        body={`The checkout deadline for "${clubName}" has expired and it will be removed from your basket. You will need to add this item to the basket again to make a payment.`}
        buttonText="Ok"
        onButtonClick={this.closeDeadlineTimeModal}
        onCloseIconClick={this.closeDeadlineTimeModal}
      />
    );
  };

  openDeadlineTimeModal = () => {
    this.setState({
      isDeadlineTimeModalOpen: true
    });
  };

  closeDeadlineTimeModal = () => {
    this.setState({
      isDeadlineTimeModalOpen: false
    });

    this.setBasket().then(() => this.setBasketRequestInterval());
  };

  renderConfirmationRemoveFromBasketModal = () => {
    const { isConfirmationRemoveFromBasketModalOpen, orderId } = this.state;

    return (
      <SimpleModal
        isOpen={isConfirmationRemoveFromBasketModalOpen}
        title="Confirm the action"
        body="You are going to remove the item from your basket. Are you sure?"
        buttonText="Confirm"
        onButtonClick={() => this.onDeleteOrderFromBasketClick(orderId)}
        onCloseClick={this.closeConfirmationRemoveFromBasketModal}
      />
    );
  };

  openConfirmationRemoveFromBasketModal = (orderId: string) => {
    this.setState({
      isConfirmationRemoveFromBasketModalOpen: true,
      orderId
    });
  };

  closeConfirmationRemoveFromBasketModal = () => {
    this.setState({
      isConfirmationRemoveFromBasketModalOpen: false,
      orderId: undefined
    });
  };

  render() {
    const { basket, isLoading } = this.state;

    const orderAmount = propz.get(basket, ['orderAmount'], 0);
    const basketOrders = propz.get(basket, ['items'], []);

    const basketOrdersCount = basketOrders.length;
    const isOrdersExist = basketOrders.length > 0;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <main className="parentPage parentBasketPage">
        {this.renderConfirmationRemoveFromBasketModal()}
        {this.renderDeadlineTimeModal()}

        <ParentPageHeader
          title="Your basket"
          subtitle="Review club bookings before continuing to secure payment."
          chips={[
            { value: basketOrdersCount, label: basketOrdersCount === 1 ? 'booking' : 'bookings', tone: basketOrdersCount ? 'blue' : 'slate' },
            ...(isOrdersExist ? [{ value: `£${orderAmount}`, label: 'total', tone: 'green' as const }] : [])
          ]}
        />

        <div className="parentBasketPage__layout">
          {isOrdersExist ? (
            <>
              <section className="parentBasketPage__orders" aria-label="Bookings in basket">
                {basketOrders.map(order => {
                  return (
                    <div key={order.id}>
                      <BasketOrder
                        order={order}
                        onDeleteOrderFromBasketClick={orderId => this.openConfirmationRemoveFromBasketModal(orderId)}
                      />
                    </div>
                  );
                })}
              </section>

              <aside className="parentCard parentBasketPage__summary">
                <span className="parentBasketPage__summaryLabel">Order summary</span>
                <div className="parentBasketPage__total">
                  <span>{basketOrdersCount === 1 ? '1 booking' : `${basketOrdersCount} bookings`}</span>
                  <strong>£{orderAmount}</strong>
                </div>
                <button type="button" className="parentButton parentButton--primary" onClick={this.onCheckoutClick}>
                  Proceed to checkout
                </button>
                <div className="parentBasketPage__secureNote">
                  <strong>Secure checkout</strong>
                  <span>Payment is completed on Stripe. Squad In Touch does not see or store your card details.</span>
                </div>
                <p className="parentBasketPage__feeNote">Booking fees are included and are non-refundable if the school later issues a refund.</p>
              </aside>
            </>
          ) : (
            <div className="parentListEmpty parentBasketPage__empty">
              <strong>Your basket is empty</strong>
              <span>Club bookings that require payment will appear here until checkout.</span>
              <button type="button" className="parentButton parentButton--primary" onClick={() => this.props.history.push('/clubProgrammes')}>
                Browse clubs
              </button>
            </div>
          )}
        </div>
      </main>
    );
  }
}

